import * as React from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'

import { cn } from '@dc-shared-web/lib'

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
    animated?: boolean
    muted?: boolean
    shimmer?: boolean
  }
>(({ className, animated, muted, shimmer, value, ...props }, ref) => {
  const [animatedValue, setAnimatedValue] = React.useState(0)
  const animatedMax = 100
  const animatedInc = animatedMax / 5
  const animatedMin = 0

  React.useEffect(() => {
    if (animated) {
      const timeout = setTimeout(() => {
        if (animatedValue + animatedInc > animatedMax) {
          setAnimatedValue(animatedMin)
        } else {
          setAnimatedValue(animatedValue + animatedInc)
        }
      }, 300)

      return () => {
        clearInterval(timeout)
      }
    }
  }, [animated, animatedMax, animatedInc, animatedMin, animatedValue]) // Add animatedValue.current as a dependency

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        'tw-relative tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full',
        muted ? 'tw-bg-neutral-100' : 'tw-bg-secondary',
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          'tw-h-full tw-w-full tw-flex-1 tw-transition-all tw-ease-out',
          shimmer
            ? 'tw-bg-primary tw-animate-shimmer'
            : muted
              ? 'tw-bg-neutral-100 tw-animate-pulse'
              : 'tw-bg-primary tw-duration-300'
        )}
        style={{
          transform: `translateX(-${100 - (value || animatedValue)}%)`,
        }}
      />
    </ProgressPrimitive.Root>
  )
})
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
